/**
 * Highlight Adapter
 *
 * Converts ShareTag/HighlightTag data structures to PositionedPhrase format
 * for use with the unified Editor and its highlight plugins.
 */

import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { getCriteriaCategoryColor } from "@markeroo/theme";
import {
  navigateToSpan,
  PathNavigationError,
} from "../../utils/jsonPathNavigation";
import type { PositionedPhrase, EditBehavior } from "../plugins/positionedPhrasePlugin";
import type { HighlightRenderData } from "../plugins/highlightDecorationPlugin";

/**
 * Tag structure from the share/highlight system.
 * This matches HighlightTag from HighlightExtension and ShareTag from @markeroo/shared.
 */
export interface HighlightTagInput {
  tag_id: string;
  startPath: number[];
  startChar: number;
  endPath: number[];
  endChar: number;
  color?: string | null;
  label?: string | null;
  explanation: string;
  suggestion?: string | null;
  humanDescription?: string | null;
  phrase?: string | null;
  beforeContext?: string | null;
  afterContext?: string | null;
  // Criteria category for color fallback
  category?: string | null;
  // For teacher comments
  isTeacherComment?: boolean;
  teacherCommentId?: string;
  teacherName?: string;
  createdBy?: string;
  // Display style (from ShareTag)
  display_style?: "demonstrative" | "full";
}

/**
 * Palette entry for highlight colors/labels.
 */
export interface HighlightPaletteEntry {
  key: string;
  color: string;
  label?: string | null;
  humanDescription?: string | null;
  category?: string | null;
}

/**
 * Extended data stored in PositionedPhrase.data for highlights.
 */
export interface HighlightPhraseData {
  tag_id: string;
  startPath: number[];
  startChar: number;
  endPath: number[];
  endChar: number;
  color: string;
  label: string;
  explanation: string;
  suggestion?: string | null;
  humanDescription?: string | null;
  phrase?: string | null;
  beforeContext?: string | null;
  afterContext?: string | null;
  display_style?: "demonstrative" | "full";
  // Teacher comment fields
  isTeacherComment?: boolean;
  teacherCommentId?: string;
  teacherName?: string;
  createdBy?: string;
}

/**
 * Generate a unique ID for a highlight tag.
 * Format: "{tag_id}-{startPath.join(',')}-{startChar}"
 */
export function getHighlightId(tag: HighlightTagInput): string {
  return `${tag.tag_id}-${tag.startPath.join(",")}-${tag.startChar}`;
}

/**
 * Convert a list of highlight tags to PositionedPhrase format.
 *
 * @param doc - The ProseMirror document to compute positions against
 * @param tags - List of highlight tags to convert
 * @param palette - Color/label palette for looking up tag styling
 * @param editBehavior - How highlights should respond to document edits
 * @returns List of PositionedPhrase objects
 */
export function tagsToPositionedPhrases(
  doc: ProseMirrorNode,
  tags: HighlightTagInput[],
  palette: HighlightPaletteEntry[] = [],
  editBehavior: EditBehavior = "atomic"
): PositionedPhrase[] {
  const paletteMap = new Map(palette.map((p) => [p.key, p]));
  const phrases: PositionedPhrase[] = [];

  for (const tag of tags) {
    try {
      const positions = navigateToSpan(
        doc,
        tag.startPath,
        tag.startChar,
        tag.endPath,
        tag.endChar
      );

      const paletteEntry = paletteMap.get(tag.tag_id);
      // Color resolution priority:
      // 1. Tag's explicit color (from tag data)
      // 2. Palette entry's color (from tag definition)
      // 3. Category-based color (from criteria category)
      // 4. Default "other" category color (slate gray)
      const category = tag.category || paletteEntry?.category;
      const color = tag.color || paletteEntry?.color || getCriteriaCategoryColor(category);
      const label = tag.label || paletteEntry?.label || tag.tag_id;
      const humanDescription =
        tag.humanDescription || paletteEntry?.humanDescription || null;

      const data: HighlightPhraseData = {
        tag_id: tag.tag_id,
        startPath: tag.startPath,
        startChar: tag.startChar,
        endPath: tag.endPath,
        endChar: tag.endChar,
        color,
        label,
        explanation: tag.explanation,
        suggestion: tag.suggestion,
        humanDescription,
        phrase: tag.phrase,
        beforeContext: tag.beforeContext,
        afterContext: tag.afterContext,
        display_style: tag.display_style,
        isTeacherComment: tag.isTeacherComment,
        teacherCommentId: tag.teacherCommentId,
        teacherName: tag.teacherName,
        createdBy: tag.createdBy,
      };

      phrases.push({
        id: getHighlightId(tag),
        startPos: positions.from,
        endPos: positions.to,
        data,
        editBehavior,
      });
    } catch (error) {
      if (error instanceof PathNavigationError) {
        console.warn(
          `Failed to navigate to highlight path for tag ${tag.tag_id}:`,
          error.message
        );
        continue;
      }
      throw error;
    }
  }

  return phrases;
}

/**
 * Get render data for a positioned phrase.
 * Used by highlightDecorationPlugin to style the decorations.
 */
export function getHighlightRenderData(
  phrase: PositionedPhrase
): HighlightRenderData {
  const data = phrase.data as HighlightPhraseData;
  return {
    id: phrase.id,
    color: data.color,
    label: data.label,
    data: data,
  };
}

/**
 * Filter tags based on visibility conditions.
 *
 * @param tags - All tags to filter
 * @param options - Filter options
 * @returns Filtered tags
 */
export function filterVisibleTags(
  tags: HighlightTagInput[],
  options: {
    /** Set of removed label IDs */
    removedSet?: Set<string>;
    /** Whether to show all markup or only demonstrative */
    showAllMarkup?: boolean;
    /** Currently selected highlight ID (show only this if set) */
    selectedHighlightId?: string | null;
    /** Legend key being hovered (show only matching tag_id if set) */
    hoveredLegendKey?: string | null;
    /** Whether viewing as teacher */
    isTeacherView?: boolean;
    /** Whether to show completed highlights in doc */
    showCompletedInDoc?: boolean;
    /** Set of completed highlight IDs */
    completedHighlightIdSet?: Set<string>;
  } = {}
): HighlightTagInput[] {
  const {
    removedSet = new Set(),
    showAllMarkup = false,
    selectedHighlightId = null,
    hoveredLegendKey = null,
    isTeacherView = false,
    showCompletedInDoc = true,
    completedHighlightIdSet = new Set(),
  } = options;

  return tags.filter((tag) => {
    const labelId = getHighlightId(tag);

    // Filter out removed highlights
    if (removedSet.has(labelId)) {
      return false;
    }

    // Hide completed AI highlights in student/share view when none selected
    if (!isTeacherView && !selectedHighlightId && !showCompletedInDoc) {
      if (completedHighlightIdSet.has(labelId)) {
        return false;
      }
    }

    // If showAllMarkup is false, only show "demonstrative" tags
    if (!showAllMarkup && tag.display_style === "full") {
      return false;
    }

    // If a highlight is selected, only show that specific highlight
    if (selectedHighlightId) {
      return labelId === selectedHighlightId;
    }

    // If hovering over a legend key, only show highlights matching that tag_id
    if (hoveredLegendKey) {
      return tag.tag_id === hoveredLegendKey;
    }

    return true;
  });
}

/**
 * Filter teacher comments based on visibility conditions.
 */
export function filterVisibleTeacherComments(
  teacherComments: HighlightTagInput[],
  options: {
    /** Currently selected highlight ID (show only this if set) */
    selectedHighlightId?: string | null;
    /** Legend key being hovered (hide all if set - comments don't belong to criteria) */
    hoveredLegendKey?: string | null;
    /** Whether viewing as teacher */
    isTeacherView?: boolean;
    /** Whether to show completed highlights in doc */
    showCompletedInDoc?: boolean;
    /** Set of completed highlight IDs */
    completedHighlightIdSet?: Set<string>;
  } = {}
): HighlightTagInput[] {
  const {
    selectedHighlightId = null,
    hoveredLegendKey = null,
    isTeacherView = false,
    showCompletedInDoc = true,
    completedHighlightIdSet = new Set(),
  } = options;

  // If hovering over a legend key, hide all teacher comments
  if (hoveredLegendKey) {
    return [];
  }

  return teacherComments.filter((comment) => {
    const commentSpanId = getHighlightId(comment);

    // Hide completed teacher comments in student/share view when none selected
    if (!isTeacherView && !selectedHighlightId && !showCompletedInDoc) {
      if (completedHighlightIdSet.has(commentSpanId)) {
        return false;
      }
    }

    // If a highlight is selected, only show that specific teacher comment
    if (selectedHighlightId) {
      return commentSpanId === selectedHighlightId;
    }

    return true;
  });
}
